import React from "react";

const FONT = "Inter, ui-sans-serif, system-ui, sans-serif";
const DISPLAY = "'Space Grotesk', Inter, sans-serif";
const Frame = ({ children, label, testid, className }) => (
  <svg viewBox="0 0 480 400" xmlns="http://www.w3.org/2000/svg" className={className || "w-full h-full"} role="img" aria-label={label} data-testid={testid}>
    <defs>
      <filter id="lo-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0E0E10" floodOpacity="0.10" />
      </filter>
    </defs>
    <rect width="480" height="400" fill="#FFFFFF" />
    {children}
  </svg>
);
const Card = ({ x, y, w, h, children, fill = "#FFFFFF" }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width={w} height={h} rx="14" fill={fill} stroke={fill === "#FFFFFF" ? "#E6E6E0" : "none"} />
    {children}
  </g>
);
const Label = ({ x, y, children, color = "#6B6B73" }) => (
  <text x={x} y={y} fontFamily={FONT} fontSize="8.5" fontWeight="700" fill={color} letterSpacing="1.2">
    {children}
  </text>
);
const Pill = ({ x, y, w, text, color, light }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width={w} height="16" rx="8" fill={color} opacity={light ? 0.14 : 1} />
    <text x={w / 2} y="11" textAnchor="middle" fontFamily={FONT} fontSize="7.5" fontWeight="700" fill={light ? color : "#FFF"}>
      {text}
    </text>
  </g>
);

/* Hero: live dispatch board — orders flow from channels through pool into two lanes */
export const DispatchBoardVisual = ({ className }) => {
  const channels = ["App", "Web", "Call centre", "Marketplace", "ERP"];
  const onDemand = [
    ["#4821 · Pharmacy · JLT", "45-min SLA · 12 min left", "#E5484D"],
    ["#4822 · Café · Marina", "Rider Omar · 1.8 km", "#4C30FF"],
    ["#4823 · Pharmacy · JLT", "Pooled with #4822", "#16A34A"],
  ];
  const scheduled = [
    ["Route 12 · 38 stops", "06:00 · VAN-17 · 92% load", "#4C30FF"],
    ["Route 13 · 41 stops", "06:30 · TRK-204 · 88% load", "#4C30FF"],
    ["Route 14 · 27 stops", "Cut-off 20:00 · planning…", "#F59E0B"],
  ];
  return (
    <Frame label="Live dispatch board: orders from every channel pooled into on-demand and scheduled lanes" testid="dispatch-board-visual" className={className}>
      <Label x={20} y={28}>ORDER SOURCES</Label>
      {channels.map((c, i) => {
        const y = 44 + i * 30;
        const path = `M96 ${y + 9} C 118 ${y + 9}, 118 100, 140 100`;
        return (
          <g key={c}>
            <path d={path} fill="none" stroke="#E6E6E0" strokeWidth="1.3" />
            <circle r="3" fill="#4C30FF">
              <animateMotion dur={`${1.8 + i * 0.3}s`} begin={`${i * 0.25}s`} repeatCount="indefinite" path={path} />
            </circle>
            <g transform={`translate(20 ${y})`}>
              <rect width="76" height="18" rx="9" fill="#F4F4F0" />
              <text x="38" y="12.5" textAnchor="middle" fontFamily={FONT} fontSize="8" fontWeight="600" fill="#2A2A30">
                {c}
              </text>
            </g>
          </g>
        );
      })}
      {/* pool */}
      <g transform="translate(140 64)" filter="url(#lo-shadow)">
        <rect width="92" height="72" rx="16" fill="#0E0E10" />
        <text x="46" y="26" textAnchor="middle" fontFamily={FONT} fontSize="7.5" fontWeight="700" fill="#C9C4FF" letterSpacing="1">
          ORDER POOL
        </text>
        <text x="46" y="50" textAnchor="middle" fontFamily={DISPLAY} fontSize="22" fontWeight="700" fill="#FFF">
          184
        </text>
        <text x="46" y="63" textAnchor="middle" fontFamily={FONT} fontSize="7" fill="#9A9AA8">
          today · 2 modes
        </text>
      </g>
      {/* pool -> lanes */}
      {[[232, 100, 262, 60], [232, 100, 262, 235]].map(([x1, y1, x2, y2], i) => {
        const path = `M${x1} ${y1} C 248 ${y1}, 248 ${y2}, ${x2} ${y2}`;
        return (
          <g key={i}>
            <path d={path} fill="none" stroke="#E6E6E0" strokeWidth="1.3" />
            <circle r="3" fill={i ? "#F59E0B" : "#E5484D"}>
              <animateMotion dur="1.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" path={path} />
            </circle>
          </g>
        );
      })}
      {/* lanes */}
      {[
        ["ON-DEMAND · NOW", onDemand, 22, "#E5484D"],
        ["SCHEDULED · TOMORROW", scheduled, 197, "#F59E0B"],
      ].map(([title, rows, y, c]) => (
        <Card key={title} x={262} y={y} w={198} h={160}>
          <circle cx="16" cy="18" r="3.5" fill={c}>
            <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <Label x={24} y={21}>{title}</Label>
          {rows.map(([a, b, cc], i) => (
            <g key={a} transform={`translate(12 ${34 + i * 40})`} opacity="0">
              <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.3 + i * 0.3}s`} fill="freeze" />
              <rect width="174" height="34" rx="8" fill="#FBFBF9" stroke="#EEEEE9" />
              <rect x="0" y="8" width="3" height="18" rx="1.5" fill={cc} />
              <text x="12" y="14" fontFamily={FONT} fontSize="8.5" fontWeight="600" fill="#0E0E10">
                {a}
              </text>
              <text x="12" y="26" fontFamily={FONT} fontSize="7.5" fill="#6B6B73">
                {b}
              </text>
            </g>
          ))}
        </Card>
      ))}
      {/* bottom: shared outcomes */}
      <g transform="translate(20 364)">
        {["One driver app", "One proof of delivery", "One billing run"].map((t, i) => (
          <g key={t} transform={`translate(${i * 150} 0)`}>
            <circle cx="6" cy="8" r="3" fill="#16A34A" />
            <text x="14" y="11.5" fontFamily={FONT} fontSize="8.5" fontWeight="600" fill="#2A2A30">
              {t}
            </text>
          </g>
        ))}
      </g>
      {/* delivered pulse */}
      <g transform="translate(20 300)" filter="url(#lo-shadow)">
        <rect width="212" height="48" rx="12" fill="#FFFFFF" stroke="#E6E6E0" />
        <Label x={14} y={18}>DELIVERED TODAY</Label>
        <text x="14" y="40" fontFamily={DISPLAY} fontSize="18" fontWeight="700" fill="#0E0E10">
          1,284
        </text>
        <text x="86" y="40" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#16A34A">
          96% on time · 100% POD
        </text>
      </g>
    </Frame>
  );
};

/* Order aggregation: orders from every channel land as pins on one city map, grouped by zone */
export const OrdersVisual = ({ className }) => {
  const pins = [
    [70, 120, "App", "#E5484D"], [112, 96, "Web", "#4C30FF"], [150, 140, "Call", "#E5484D"], [98, 170, "Market", "#E5484D"],
    [250, 90, "ERP", "#4C30FF"], [290, 130, "Web", "#4C30FF"], [330, 100, "CSV", "#4C30FF"], [272, 170, "App", "#E5484D"],
    [160, 250, "App", "#E5484D"], [210, 290, "Market", "#E5484D"], [120, 300, "ERP", "#4C30FF"], [190, 320, "Web", "#4C30FF"],
    [360, 240, "CSV", "#4C30FF"], [400, 290, "ERP", "#4C30FF"], [340, 310, "Call", "#E5484D"], [420, 200, "App", "#E5484D"],
  ];
  const zones = [
    [40, 70, 150, 130, "MARINA · 58", 0], [230, 60, 140, 140, "DOWNTOWN · 47", 1], [90, 220, 160, 130, "AL QUOZ · 41", 2], [320, 180, 130, 160, "JLT · 38", 3],
  ];
  return (
    <Frame label="Orders from every channel aggregated on one map, grouped by zone" testid="visual-orders" className={className}>
      <defs>
        <pattern id="ov-grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M24 0H0V24" fill="none" stroke="#EEEEE9" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="480" height="400" fill="url(#ov-grid)" />
      <g stroke="#E6E6E0" strokeWidth="8" strokeLinecap="round" fill="none">
        <path d="M0 210 H480" />
        <path d="M205 0 V400" />
        <path d="M0 60 Q 240 40 480 80" />
      </g>
      {zones.map(([x, y, w, h, l, i]) => (
        <g key={l} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.6s" begin={`${1.2 + i * 0.2}s`} fill="freeze" />
          <rect x={x} y={y} width={w} height={h} rx="16" fill="#4C30FF" opacity="0.05" />
          <rect x={x} y={y} width={w} height={h} rx="16" fill="none" stroke="#4C30FF" strokeWidth="1.5" strokeDasharray="6 6" opacity="0.5" />
          <rect x={x + 10} y={y - 9} width={l.length * 5.6 + 12} height="18" rx="9" fill="#0E0E10" />
          <text x={x + 16} y={y + 3.5} fontFamily={FONT} fontSize="8" fontWeight="700" fill="#FFF" letterSpacing="0.8">
            {l}
          </text>
        </g>
      ))}
      {pins.map(([x, y, ch, c], i) => (
        <g key={i} transform={`translate(${x} ${y})`} filter="url(#lo-shadow)" opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.35s" begin={`${0.1 + i * 0.09}s`} fill="freeze" />
          <animateTransform attributeName="transform" type="translate" from={`${x} ${y - 24}`} to={`${x} ${y}`} dur="0.35s" begin={`${0.1 + i * 0.09}s`} fill="freeze" />
          <path d="M0 10 C -8 0, -8 -8, 0 -15 C 8 -8, 8 0, 0 10 Z" fill={c} />
          <circle cy="-6" r="3" fill="#FFF" />
          <rect x="8" y="-14" width={ch.length * 5 + 8} height="13" rx="6.5" fill="#FFF" stroke="#E6E6E0" />
          <text x={12} y="-4.5" fontFamily={FONT} fontSize="7" fontWeight="700" fill="#2A2A30">
            {ch}
          </text>
        </g>
      ))}
      <g transform="translate(16 352)" filter="url(#lo-shadow)">
        <rect width="448" height="34" rx="12" fill="#FFFFFF" stroke="#E6E6E0" />
        <text x="14" y="21.5" fontFamily={DISPLAY} fontSize="13" fontWeight="700" fill="#0E0E10">
          184 orders
        </text>
        <text x="96" y="21.5" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#6B6B73">
          6 channels · one pool
        </text>
        <circle cx="236" cy="17" r="4" fill="#E5484D" />
        <text x="246" y="21" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#2A2A30">
          112 on-demand
        </text>
        <circle cx="340" cy="17" r="4" fill="#4C30FF" />
        <text x="350" y="21" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#2A2A30">
          72 scheduled
        </text>
      </g>
    </Frame>
  );
};

/* Problem: one order's journey through six tools, with the leak at every hand-off */
export const HandoffLeakVisual = ({ className }) => {
  const rows = [
    ["09:14", "Order received", "Portal", null],
    [null, null, null, "+38 min · waiting in a queue nobody watches"],
    ["09:52", "Dispatched", "Chat group", null],
    [null, null, null, "+22 min · route built by hand"],
    ["10:14", "Routed", "Spreadsheet", null],
    [null, null, null, "Wrong address · 2 calls to the driver"],
    ["10:40", "Driver en route", "Phone", null],
    [null, null, null, "3 “where is my order?” calls"],
    ["11:26", "Delivered", "Doorbell", null],
    [null, null, null, "No proof · 3 days to match the invoice"],
    ["Day 3", "Invoiced", "Month-end", null],
  ];
  return (
    <Frame label="One order's journey through six disconnected tools, leaking time at every hand-off" testid="visual-handoffs" className={className}>
      <Card x={24} y={24} w={272} h={352}>
        <Label x={18} y={28}>ORDER #4821 · ONE DELIVERY, SIX TOOLS</Label>
        <line x1="54" y1="46" x2="54" y2="312" stroke="#E6E6E0" strokeWidth="2" />
        {rows.map(([t, a, tool, leak], i) => {
          const y = 44 + i * 25.5;
          return (
            <g key={i} transform={`translate(18 ${y})`} opacity="0">
              <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.15 + i * 0.22}s`} fill="freeze" />
              {leak ? (
                <>
                  <line x1="36" y1="0" x2="36" y2="18" stroke="#E5484D" strokeWidth="2" strokeDasharray="3 3" />
                  <rect x="50" y="1" width="186" height="16" rx="8" fill="#FEF2F2" stroke="#FECACA" />
                  <text x="58" y="12" fontFamily={FONT} fontSize="7.5" fontWeight="700" fill="#B91C1C">
                    {leak}
                  </text>
                </>
              ) : (
                <>
                  <text x="0" y="12" fontFamily={FONT} fontSize="8" fontWeight="600" fill="#6B6B73">
                    {t}
                  </text>
                  <circle cx="36" cy="9" r="5" fill="#FFF" stroke="#0E0E10" strokeWidth="2" />
                  <text x="50" y="12.5" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#0E0E10">
                    {a}
                  </text>
                  <Pill x={236 - (tool.length * 5 + 12)} y={1} w={tool.length * 5 + 12} text={tool} color="#6B6B73" light />
                </>
              )}
            </g>
          );
        })}
        <rect x="18" y="332" width="236" height="4" rx="2" fill="#F4F4F0" />
        <rect x="18" y="332" width="0" height="4" rx="2" fill="#E5484D">
          <animate attributeName="width" from="0" to="150" dur="2.4s" begin="0.4s" fill="freeze" />
        </rect>
        <text x="254" y="347" textAnchor="end" fontFamily={FONT} fontSize="7.5" fontWeight="600" fill="#B91C1C">
          64% of the day lost between tools
        </text>
      </Card>
      <Card x={312} y={24} w={144} h={108}>
        <Label x={14} y={24}>TIME LOST · TODAY</Label>
        <text x="14" y="56" fontFamily={DISPLAY} fontSize="22" fontWeight="700" fill="#0E0E10">
          4 h 12 m
        </text>
        <text x="14" y="72" fontFamily={FONT} fontSize="8" fill="#6B6B73">
          across 184 orders
        </text>
        <rect x="14" y="84" width="116" height="5" rx="2.5" fill="#F4F4F0" />
        <rect x="14" y="84" width="0" height="5" rx="2.5" fill="#E5484D">
          <animate attributeName="width" from="0" to="88" dur="1.4s" begin="0.6s" fill="freeze" />
        </rect>
      </Card>
      <Card x={312} y={146} w={144} h={108}>
        <Label x={14} y={24}>REATTEMPTS</Label>
        <text x="14" y="56" fontFamily={DISPLAY} fontSize="22" fontWeight="700" fill="#0E0E10">
          1 in 5
        </text>
        <text x="14" y="72" fontFamily={FONT} fontSize="8" fill="#6B6B73">
          no ETA shared · customer out
        </text>
        {[0, 1, 2, 3, 4].map((i) => (
          <rect key={i} x={14 + i * 24} y="84" width="18" height="6" rx="3" fill={i === 4 ? "#E5484D" : "#E6E6E0"} />
        ))}
      </Card>
      <Card x={312} y={268} w={144} h={108} fill="#0E0E10">
        <Label x={14} y={24} color="#C9C4FF">
          TRUST
        </Label>
        {[["14", "calls waiting"], ["2", "disputes, no proof"], ["0", "merchants invoiced"]].map(([v, l], i) => (
          <g key={l} transform={`translate(14 ${44 + i * 20})`}>
            <text y="10" fontFamily={DISPLAY} fontSize="12" fontWeight="700" fill="#FFF">
              {v}
            </text>
            <text x="22" y="10" fontFamily={FONT} fontSize="8.5" fill="#B8B8C4">
              {l}
            </text>
          </g>
        ))}
        <circle cx="130" cy="20" r="3.5" fill="#E5484D">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" />
        </circle>
      </Card>
    </Frame>
  );
};

/* Dispatch rules */
export const DispatchVisual = ({ className }) => (
  <Frame label="Automatic dispatch matching orders to branches and riders" testid="visual-dispatch" className={className}>
    <Card x={24} y={24} w={256} h={352}>
      <Label x={18} y={28}>AUTO-DISPATCH · #4821</Label>
      {[
        ["Nearest branch with stock", "Branch JLT-02 · 1.2 km", true],
        ["Best-placed rider", "Omar K. · 3 min away · 1 order", true],
        ["Pooling", "Paired with #4823 (same tower)", true],
        ["SLA check", "ETA 22 min · within 45-min SLA", true],
        ["Overflow rule", "3PL if no rider in 5 min", false],
      ].map(([a, b, done], i) => (
        <g key={a} transform={`translate(18 ${50 + i * 60})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.45s" begin={`${0.2 + i * 0.3}s`} fill="freeze" />
          <circle cx="9" cy="9" r="9" fill={done ? "#16A34A" : "#F4F4F0"} />
          {done ? <path d="M5 9.5 L8 12.5 L13.5 6.5" fill="none" stroke="#FFF" strokeWidth="1.8" strokeLinecap="round" /> : <circle cx="9" cy="9" r="3" fill="#9A9AA8" />}
          <text x="26" y="8" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#0E0E10">
            {a}
          </text>
          <text x="26" y="22" fontFamily={FONT} fontSize="8.5" fill="#6B6B73">
            {b}
          </text>
        </g>
      ))}
    </Card>
    <Card x={296} y={24} w={160} h={168}>
      <Label x={16} y={26}>RIDERS ONLINE</Label>
      <text x="16" y="66" fontFamily={DISPLAY} fontSize="30" fontWeight="700" fill="#0E0E10">
        42
      </text>
      <text x="16" y="84" fontFamily={FONT} fontSize="8.5" fill="#6B6B73">
        31 on task · 11 free
      </text>
      {[0.8, 0.95, 0.6, 0.7, 0.9, 0.5, 0.75].map((v, i) => (
        <rect key={i} x={16 + i * 19} y={150 - v * 46} width="12" height={v * 46} rx="3" fill="#4C30FF" opacity={0.35 + v * 0.6}>
          <animate attributeName="height" from="0" to={v * 46} dur="1s" begin={`${0.3 + i * 0.08}s`} fill="freeze" />
          <animate attributeName="y" from="150" to={150 - v * 46} dur="1s" begin={`${0.3 + i * 0.08}s`} fill="freeze" />
        </rect>
      ))}
    </Card>
    <Card x={296} y={208} w={160} h={168} fill="#0E0E10">
      <Label x={16} y={26} color="#C9C4FF">
        ASSIGNED
      </Label>
      <text x="16" y="62" fontFamily={DISPLAY} fontSize="16" fontWeight="700" fill="#FFF">
        Omar K.
      </text>
      <text x="16" y="80" fontFamily={FONT} fontSize="8.5" fill="#B8B8C4">
        #4821 + #4823 · pooled
      </text>
      <text x="16" y="112" fontFamily={FONT} fontSize="8.5" fill="#B8B8C4">
        Pickup · Branch JLT-02
      </text>
      <text x="16" y="128" fontFamily={FONT} fontSize="8.5" fill="#B8B8C4">
        2 drops · 4.6 km · ETA 22 min
      </text>
      <circle cx="140" cy="22" r="4" fill="#25D366">
        <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
      </circle>
    </Card>
  </Frame>
);

/* Route plan: stop sequence + load */
export const RoutePlanVisual = ({ className }) => (
  <Frame label="Optimised route plan with stop sequence and vehicle load" testid="visual-route-plan" className={className}>
    <Card x={24} y={24} w={272} h={352}>
      <Label x={18} y={28}>ROUTE 12 · VAN-17 · 38 STOPS</Label>
      <text x="254" y="28" textAnchor="end" fontFamily={FONT} fontSize="8.5" fontWeight="700" fill="#16A34A">
        -31% km vs manual
      </text>
      <line x1="34" y1="56" x2="34" y2="340" stroke="#E6E6E0" strokeWidth="2" strokeDasharray="320" strokeDashoffset="320">
        <animate attributeName="stroke-dashoffset" from="320" to="0" dur="1.6s" fill="freeze" />
      </line>
      {[
        ["Depot · Al Quoz", "06:00 · load 92%", "#0E0E10"],
        ["Stop 1 · Business Bay", "06:40–07:00 · 3 cartons", "#4C30FF"],
        ["Stop 2 · Downtown", "07:05–07:20 · 1 carton", "#4C30FF"],
        ["Stop 3 · DIFC", "07:30–08:00 · window ✓", "#4C30FF"],
        ["Stop 4 · Jumeirah", "08:15 · reattempt from Mon", "#F59E0B"],
        ["… 33 more stops", "back at depot 15:40", "#9A9AA8"],
      ].map(([a, b, c], i) => (
        <g key={a} transform={`translate(26 ${50 + i * 50})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.2 + i * 0.22}s`} fill="freeze" />
          <circle cx="8" cy="10" r="7" fill="#FFF" stroke={c} strokeWidth="2.5" />
          <text x="26" y="9" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#0E0E10">
            {a}
          </text>
          <text x="26" y="23" fontFamily={FONT} fontSize="8.5" fill="#6B6B73">
            {b}
          </text>
        </g>
      ))}
    </Card>
    <Card x={312} y={24} w={144} h={168}>
      <Label x={16} y={26}>VEHICLE LOAD</Label>
      <rect x="16" y="44" width="112" height="100" rx="10" fill="#F4F4F0" />
      <rect x="16" y="144" width="112" height="0" rx="10" fill="#4C30FF">
        <animate attributeName="height" from="0" to="92" dur="1.4s" fill="freeze" />
        <animate attributeName="y" from="144" to="52" dur="1.4s" fill="freeze" />
      </rect>
      <text x="72" y="104" textAnchor="middle" fontFamily={DISPLAY} fontSize="22" fontWeight="700" fill="#FFF">
        92%
      </text>
      <text x="72" y="160" textAnchor="middle" fontFamily={FONT} fontSize="8" fill="#6B6B73">
        by volume · 1,180 kg
      </text>
    </Card>
    <Card x={312} y={208} w={144} h={168}>
      <Label x={16} y={26}>CONSTRAINTS</Label>
      {["Time windows", "Capacity & weight", "Driver shift 8 h", "Live traffic", "Priority orders"].map((t, i) => (
        <g key={t} transform={`translate(16 ${44 + i * 24})`}>
          <path d="M2 7 L5 10 L10.5 4" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" />
          <text x="18" y="10" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#2A2A30">
            {t}
          </text>
        </g>
      ))}
    </Card>
  </Frame>
);

/* Customer notifications */
export const NotifyVisual = ({ className }) => (
  <Frame label="Customer notifications and tracking link" testid="visual-notify" className={className}>
    <Card x={24} y={24} w={232} h={352}>
      <Label x={18} y={28}>WHATSAPP · +971 5X XXX XXXX</Label>
      {[
        ["Your order #4821 is confirmed. Track it live: frt.io/t/4821", "09:14"],
        ["Omar is on the way · ETA 22 min.", "09:31"],
        ["Omar is 2 minutes away. Please keep your Emirates ID ready for OTP hand-over.", "09:51"],
        ["Delivered · signed by Sara A. · 09:54. Rate your delivery ★★★★★", "09:54"],
      ].map(([m, t], i) => (
        <g key={t} transform={`translate(18 ${46 + i * 78})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.3 + i * 0.45}s`} fill="freeze" />
          <rect width="196" height="64" rx="12" fill="#EDEAFF" />
          <foreignObject x="10" y="8" width="176" height="42">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: FONT, fontSize: 8.5, lineHeight: "12px", color: "#0E0E10" }}>
              {m}
            </div>
          </foreignObject>
          <text x="186" y="58" textAnchor="end" fontFamily={FONT} fontSize="7" fill="#6B6B73">
            {t}
          </text>
        </g>
      ))}
    </Card>
    <Card x={272} y={24} w={184} h={352}>
      <Label x={16} y={26}>TRACKING LINK · LIVE</Label>
      <rect x="16" y="40" width="152" height="150" rx="12" fill="#F4F4F0" />
      <path d="M40 170 C 70 120, 110 150, 150 60" fill="none" stroke="#4C30FF" strokeWidth="2.5" strokeDasharray="4 5" />
      <circle r="6" fill="#4C30FF" stroke="#FFF" strokeWidth="2">
        <animateMotion dur="6s" repeatCount="indefinite" path="M40 170 C 70 120, 110 150, 150 60" />
      </circle>
      <circle cx="150" cy="60" r="5" fill="#FFF" stroke="#0E0E10" strokeWidth="2" />
      <text x="16" y="222" fontFamily={DISPLAY} fontSize="20" fontWeight="700" fill="#0E0E10">
        ETA 09:53
      </text>
      <text x="16" y="240" fontFamily={FONT} fontSize="8.5" fill="#6B6B73">
        Omar K. · 1.1 km away
      </text>
      <g transform="translate(16 262)">
        <rect width="152" height="30" rx="15" fill="#0E0E10" />
        <text x="76" y="19" textAnchor="middle" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#FFF">
          Reschedule delivery
        </text>
      </g>
      <g transform="translate(16 302)">
        <rect width="152" height="30" rx="15" fill="#FFF" stroke="#E6E6E0" />
        <text x="76" y="19" textAnchor="middle" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#0E0E10">
          Add delivery note
        </text>
      </g>
    </Card>
  </Frame>
);

/* Billing & analytics */
export const BillingVisual = ({ className }) => (
  <Frame label="Merchant billing and delivery analytics" testid="visual-billing" className={className}>
    <Card x={24} y={24} w={272} h={352}>
      <Label x={18} y={28}>MERCHANT STATEMENTS</Label>
      <Pill x={158} y={16} w={96} text="Generated in 4 min" color="#16A34A" light />
      {[
        ["Pharmacy chain", "3,842 tasks", "AED 48,025", 1],
        ["Restaurant group", "1,190 tasks", "AED 14,280", 0.37],
        ["Gift marketplace", "2,406 tasks", "AED 31,278", 0.65],
        ["Fashion retailer", "812 tasks", "AED 11,368", 0.24],
        ["FMCG distributor", "1,955 tasks", "AED 21,505", 0.45],
      ].map(([n, t, amt, v], i) => (
        <g key={n} transform={`translate(18 ${52 + i * 58})`}>
          <text y="10" fontFamily={FONT} fontSize="10" fontWeight="600" fill="#0E0E10">
            {n}
          </text>
          <text x="236" y="10" textAnchor="end" fontFamily={FONT} fontSize="10" fontWeight="700" fill="#0E0E10">
            {amt}
          </text>
          <text y="24" fontFamily={FONT} fontSize="8.5" fill="#6B6B73">
            {`${t} · SLA 97%`}
          </text>
          <rect y="32" width="236" height="5" rx="2.5" fill="#F4F4F0" />
          <rect y="32" width="0" height="5" rx="2.5" fill="#4C30FF">
            <animate attributeName="width" from="0" to={v * 236} dur="1.2s" begin={`${0.2 + i * 0.12}s`} fill="freeze" />
          </rect>
        </g>
      ))}
    </Card>
    {[
      ["COST PER DROP", "AED 12.4", "-25% vs Q1", "#16A34A"],
      ["ON-TIME RATE", "96.2%", "target 95%", "#16A34A"],
      ["REATTEMPTS", "3.1%", "-30% vs Q1", "#16A34A"],
      ["WISMO CALLS", "-41%", "since tracking links", "#4C30FF"],
    ].map(([l, v, d, c], i) => (
      <Card key={l} x={312 + (i % 2) * 76 - (i % 2) * 76} y={24 + i * 90} w={144} h={78}>
        <Label x={14} y={22}>{l}</Label>
        <text x="14" y="50" fontFamily={DISPLAY} fontSize="20" fontWeight="700" fill="#0E0E10">
          {v}
        </text>
        <text x="14" y="66" fontFamily={FONT} fontSize="8" fontWeight="600" fill={c}>
          {d}
        </text>
      </Card>
    ))}
  </Frame>
);
